import {
  TableBody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Table,
  TableActionTh,
  TableActionTd,
} from '@widgets/table';
import Paginator, { DEFAULT_PER_PAGE } from '@widgets/paginator';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import Section from '@widgets/components/section';
import { Link, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import { GrayButton } from '@shared/uikit/button';
import { ISelectOption } from '@widgets/multi-select';
import { toast } from 'react-toastify';
import { ListLoader } from '@shared/uikit/loader';
import EmptyList from '@widgets/empty-list';
import DropdownButton from '@widgets/dropdown-button';
import IconArrowUp from '@shared/icons/IconArrowUp';
import { DeletePrompt } from '@widgets/prompt';
import {
  useFilters,
  useLimit,
  useListParamsAndPersistToUrl,
  usePage,
  useSort,
} from '@app/hooks/useList';
import { useUrlParams } from '@app/hooks/useUrlParams';
import HostingListFilter, { IHostingListFilter } from '@pages/Hosting/List/filter';
import { useDeleteHosting } from '@app/queries/hosting/useDeleteHosting';
import { IHostingList, useHostingList } from '@app/queries/hosting/useHostingList';
import PageHeader from '@shared/uikit/page-header';
import { CreateClientPopup } from '@pages/Customer/Create';
import { ValidationError } from '@app/error/ValidationError';
import { HostingCreate } from '@pages/Hosting/List/Create';

const HostingList = () => {
  const dispatch = useDispatch();

  const listFetcher = useHostingList();
  const deleteFetcher = useDeleteHosting();

  const { urlParams } = useUrlParams();

  const page = usePage(urlParams, 1);
  const perPage = useLimit(urlParams, DEFAULT_PER_PAGE);
  const filtering = useFilters(urlParams, []);
  const sorting = useSort(urlParams);
  useListParamsAndPersistToUrl(page, perPage, filtering, sorting);

  const [list, setList] = useState<IHostingList | null>(null);
  const [isShowCreatePopup, setShowCreatePopup] = useState<boolean>(false);
  const [isShowDelete, setShowDelete] = useState<boolean>(false);
  const [bulkItems, setBulkItems] = useState<number[]>([]);

  const customersActions: ISelectOption[] = [{ value: 'remove', label: 'Удалить' }];

  const handleCustomersAction = (value: string, onOneRow = false) => {
    if (!bulkItems.length && !onOneRow) {
      toast.error('Необходимо выбрать элементы для массового применения действия');
      return;
    }

    switch (value) {
      case 'remove':
        setShowDelete(true);
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Хостинг',
          type: BreadCrumbType.LINK,
          link: '/hosting/main',
          icon: BreadCrumbIcon.HOSTING,
        },
        { title: 'Обзор хостингов', type: BreadCrumbType.TEXT },
      ])
    );
  }, []);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const data = await listFetcher.fetchData({
        ...filtering.value,
        page: page.value ?? 1,
        sort: sorting.value,
        perPage: perPage.value ?? DEFAULT_PER_PAGE,
      });
      setList(data);
    })();
  }, [page.value, perPage.value, filtering.value, sorting.value?.column, sorting.value?.type]);

  const handlePageClick = useCallback(
    (selectedItem: { selected: number }) => page.setValue(selectedItem.selected + 1),
    []
  );

  const onChangePerPage = useCallback(
    (count: number) => {
      perPage.setValue(count);
      setBulkItems([]);
    },
    [perPage.value]
  );

  const refreshList = async () => {
    const data = await listFetcher.fetchData({
      ...filtering.value,
      page: page.value ?? 1,
      sort: sorting.value,
      perPage: perPage.value ?? DEFAULT_PER_PAGE,
    });
    setList(data);
  };

  const onCreate = async () => {};

  const onDelete = async () => {
    if (!bulkItems.length) {
      setShowDelete(false);
      toast.error('Необходимо выбрать элементы для массового применения действия');
      return;
    }

    try {
      let successMessage =
        1 < bulkItems.length ? 'Хостинги успешно удалены' : 'Хостинг успешно удален';

      for (let id of bulkItems) {
        await deleteFetcher.fetch({ id: id });
      }

      setShowDelete(false);
      setBulkItems([]);
      toast.success(successMessage);

      await refreshList();
    } catch (e) {
      toast.error('Произошла ошибка при удалении');
    }
  };

  return (
    <>
      <PageHeader title="Обзор хостингов" />

      <div className="flex justify-between items-center w-full mb-[20px]">
        <HostingListFilter
          filters={filtering.value}
          onChangeFilters={(values: IHostingListFilter) => filtering.setValue(values)}
          onReset={() => {
            page.setValue(1);
            perPage.setValue(DEFAULT_PER_PAGE);
            sorting.setValue(null);
            filtering.setValue([]);
          }}
        />
        <div className="flex gap-[10px] pl-[10px] self-start">
          <div className="flex gap-[10px]">
            <GrayButton
              onClick={() => setShowCreatePopup(true)}
              title={<div className="flex gap-[5px] items-center fill-[white] text-[18px]">+</div>}
            />
          </div>
        </div>
      </div>

      <Section>
        <div className="flex justify-between items-center w-full mb-[10px]">
          <span className="font-semibold text-[20px]">Список хостингов</span>
          <div className="flex items-center gap-2 white">
            <DropdownButton
              title={
                <>
                  <span className={'text-[12px] mr-[10px]'}>Действие</span>
                  <IconArrowUp opacity="0.3" height="12" width="9" />
                </>
              }
              className="border border-[#262528] bg-[#211F24] px-[10px] rounded-[8px] w-full h-[33px] text-white"
              options={customersActions}
              onSelect={(value: any) => {
                handleCustomersAction(value);
              }}
            />
          </div>
        </div>

        {null !== list ? (
          <>
            {list.items.length ? (
              <>
                <Table>
                  <TableThead>
                    <TableTr>
                      <TableActionTh
                        className="w-[3%]"
                        onClick={() => {
                          if (list.items.length === bulkItems.length) {
                            setBulkItems([]);
                          } else {
                            setBulkItems(list.items.map(item => item.id));
                          }
                        }}
                      />
                      <TableTh
                        title="Название"
                        className="w-[17%]"
                        sortName="name"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Статус хостинга"
                        className="w-[15%]"
                        sortName="status"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Общая мощность"
                        className="w-[10%]"
                        sortName="common_power"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Локация размещения"
                        className="w-[15%]"
                        sortName="location"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Номинальная мощнность"
                        className="w-[10%]"
                        sortName="nominal_power"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Фактическая мощность"
                        className="w-[10%]"
                        sortName="actual_power"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Доля от общей мощности"
                        className="w-[10%]"
                        sortName="percent_of_power"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Свободная мощность"
                        className="w-[10%]"
                        sortName="free_power"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                    </TableTr>
                  </TableThead>
                  <TableBody>
                    {list &&
                      list.items.map((hosting, key) => (
                        <TableTr key={key}>
                          <TableActionTd
                            items={bulkItems}
                            value={hosting.id}
                            onChange={setBulkItems}
                          />

                          <TableTd
                            title={
                              <Link to={`/hosting/${hosting.id}`}>
                                <ValueWithSign value={hosting.name} color="bg-[#4598E6]" />
                              </Link>
                            }
                          />

                          <TableTd title={<>{hosting.status ?? '-'}</>} />
                          <TableTd title={`${hosting.common_power ?? '-'}`} />
                          <TableTd title={`${hosting.location ?? '-'}`} />
                          <TableTd title={`${hosting.nominal_power ?? '-'}`} />
                          <TableTd title={`${hosting.actual_power ?? '-'}`} />
                          <TableTd title={`${hosting.percent_of_power ?? '-'}`} />
                          <TableTd title={`${hosting.free_power ?? '-'}`} />
                        </TableTr>
                      ))}
                  </TableBody>
                </Table>

                <div className="pt-[30px] flex justify-end items-center w-full ">
                  <Paginator
                    totalPages={list.total_pages}
                    page={page.value ? page.value - 1 : 0}
                    perPage={perPage.value ?? DEFAULT_PER_PAGE}
                    onChangePerPage={onChangePerPage}
                    onChangePage={handlePageClick}
                    totalItems={`${list.total_items} хостингов`}
                  />
                </div>
              </>
            ) : (
              <EmptyList />
            )}
          </>
        ) : (
          <ListLoader loading />
        )}
      </Section>
      {isShowDelete ? (
        <DeletePrompt
          onClose={() => {
            setShowDelete(false);
          }}
          onSubmit={onDelete}
          open={true}
          title={
            <span className="text-[18px] text-[white] text-center">
              {1 < bulkItems.length ? 'Удалить выбранные хостинги' : 'Удалить выбранный хостинг'}?
            </span>
          }
          subTitle={
            <>
              {1 < bulkItems.length
                ? 'Хостинги будут удалёны из базы данных.'
                : 'Хостинг будет удалён из базы данных.'}
            </>
          }
        />
      ) : null}
      {isShowCreatePopup ? (
        <HostingCreate onClose={() => setShowCreatePopup(false)} onSubmit={onCreate} />
      ) : null}
    </>
  );
};

export default HostingList;
